import type { ReactNode } from 'react';
import { T, useTheme, dens, Card, Ico } from '../../../design';
import { fmtCount } from '../../../utils';
import { ACCENT_SOFT } from '../primeStatsConfig';
import { Avatar } from './Avatar';
import type { SalonEmployeeRow, ChainSalon } from '../../../types/primeStats.types';

export function ListCard({ title, count, children, footer }: { title: string; count?: number | string; children: ReactNode; footer?: string }) {
  const t = useTheme();
  const d = dens(t);
  return (
    <Card pad={0}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: `${d.pad}px ${d.pad}px 10px` }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.fg }}>{title}</div>
        {count != null && <div style={{ fontSize: 11, fontWeight: 700, color: t.accent, background: T.accentLight, padding: '2px 9px', borderRadius: 999 }}>{count}</div>}
      </div>
      <div>{children}</div>
      {footer && <div style={{ padding: '11px 16px', borderTop: `1px solid ${T.borderLight}`, fontSize: 11, color: T.fg3 }}>{footer}</div>}
    </Card>
  );
}

export function EmployeeRow({ row, salonTotal, onClick }: { row: SalonEmployeeRow; salonTotal: number; onClick: (() => void) | null }) {
  const t = useTheme();
  const pct = Math.round((row.total / Math.max(1, salonTotal)) * 100);
  return (
    <div onClick={onClick ?? undefined} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', borderTop: `1px solid ${T.borderLight}`, cursor: onClick ? 'pointer' : 'default' }}>
      {row.salonOwned
        ? <span style={{ width: 34, height: 34, borderRadius: 999, background: T.accentLight, color: t.accent, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Ico.store size={16} /></span>
        : <Avatar name={row.name} size={34} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: T.fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.name}</div>
        <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1, maxWidth: 120, height: 5, borderRadius: 999, background: T.track, overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: row.salonOwned ? ACCENT_SOFT : t.accent }} />
          </div>
          <span style={{ fontSize: 11, color: T.fg3, fontWeight: 600, minWidth: 30, fontVariantNumeric: 'tabular-nums', }}>{pct}%</span>
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 72, fontVariantNumeric: 'tabular-nums', }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: T.fg, letterSpacing: '-0.02em' }}>{fmtCount(row.total)}</div>
        <div style={{ fontSize: 10.5, color: T.fg3, fontWeight: 600 }}>{fmtCount(row.primary)}H · {fmtCount(row.family)}F</div>
      </div>
      {/* Pladsen til pilen reserveres altid, så tallene flugter i rækker uden drilldown */}
      <span aria-hidden style={{ color: T.fg3, flexShrink: 0, width: 16, marginLeft: -2, visibility: onClick ? 'visible' : 'hidden' }}><Ico.chevR size={16} /></span>
    </div>
  );
}

export function SalonRow({ salon, rank, max, onClick }: { salon: ChainSalon; rank: number; max: number; onClick: () => void }) {
  const t = useTheme();
  const pct = Math.round((salon.total / Math.max(1, max)) * 100);
  return (
    <div onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', borderTop: `1px solid ${T.borderLight}`, cursor: 'pointer' }}>
      <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, color: T.fg3, width: 18, flexShrink: 0, textAlign: 'right' }}>{rank}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: T.fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{salon.name}</div>
        <div style={{ marginTop: 5, height: 5, borderRadius: 999, background: T.track, overflow: 'hidden', maxWidth: 150 }}>
          <div style={{ width: `${pct}%`, height: '100%', background: t.accent }} />
        </div>
      </div>
      <div style={{ fontSize: 16, fontWeight: 800, color: T.fg, letterSpacing: '-0.02em', flexShrink: 0, minWidth: 48, textAlign: 'right', fontVariantNumeric: 'tabular-nums', }}>{fmtCount(salon.total)}</div>
      <span style={{ color: T.fg3, flexShrink: 0 }}><Ico.chevR size={16} /></span>
    </div>
  );
}
